"use client"

import { useState, useEffect, useRef } from "react"
import { getBillingInfo } from "../utils/getBillingInfo"
import { getPaymentMethods } from "../utils/getPaymentMethods"
import { createSubscription } from "../utils/createSubscription"
import { AlertCircle, CreditCard, CheckCircle, X } from "lucide-react"
import { useStripe, useElements } from "@stripe/react-stripe-js"

const modalStyles = `
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    backdrop-filter: blur(8px);
    background-color: rgba(0, 0, 0, 0.5) !important;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
  }
  .modal-content {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    max-width: 500px;
    width: 100%;
  }
  .position-relative {
    position: relative;
    display: inline-block;
  }
  .tooltip-text {
    visibility: hidden;
    width: 200px;
    background-color: #555;
    color: #fff;
    text-align: center;
    border-radius: 6px;
    padding: 5px;
    position: absolute;
    z-index: 1;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.3s, visibility 0.3s;
    white-space: normal;
    word-wrap: break-word;
  }
  .position-relative:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`

interface BillingInfo {
  is_subscribed: boolean
  subscription_object: {
    id: string
    cancel_at_period_end: boolean
    current_period_end: number
    plan: {
      interval: string
      amount: number
      currency: string
      id: string
    }
  } | null
  stripe_id: string
  next_invoice: null
  credit_balance: number
  currency: string | null
  upsell_plans: Array<{
    price_id: string
    period: string
    price: number
    currency: string
  }>
}

interface PaymentMethod {
  id: string
  card: {
    brand: string
    last4: string
    exp_month: number
    exp_year: number
  }
  default?: boolean
}

async function addCredit(amount: number) {
  try {
    const response = await fetch(process.env.REACT_APP_API_HOST + "billing/add-credit", {
      method: "POST",
      headers: {
        accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("tokenLida"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ amount }),
    })

    if (!response.ok) {
      throw new Error("Failed to add credit")
    }

    return await response.json()
  } catch (error) {
    console.error("Error adding credit:", error)
    throw error
  }
}

export default function PlanSection() {
  const stripe = useStripe()
  const elements = useElements()
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null)
  const [creditAmount, setCreditAmount] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [showPlansModal, setShowPlansModal] = useState(false)
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingCredit, setIsAddingCredit] = useState(false)
  const [isProcessingSubscription, setIsProcessingSubscription] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const [info, paymentMethodsData] = await Promise.all([getBillingInfo(), getPaymentMethods()])
        setBillingInfo(info)
        setPaymentMethods(paymentMethodsData.payment_methods)
        setError(null)
      } catch (err) {
        console.error(err)
        setError("Failed to fetch billing information")
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleAddCredit = async () => {
    if (!stripe || !elements) {
      setError("Stripe is not initialized. Please try again in a moment.")
      return
    }

    const amount = Number.parseFloat(creditAmount)
    if (isNaN(amount) || amount < 5) {
      setError("Minimum credit amount is 5 EUR")
      return
    }

    if (paymentMethods.length === 0) {
      setError("Please add a payment method first")
      return
    }

    if (paymentMethods.length > 1 && !selectedPaymentMethod) {
      setShowPaymentMethodModal(true)
      return
    }

    setIsAddingCredit(true)
    setError(null)

    try {
      const { clientSecret } = await addCredit(amount * 100) // Convert to cents

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: selectedPaymentMethod || paymentMethods[0].id,
      })

      if (result.error) {
        setError(result.error.message || "An error occurred while processing your payment")
      } else {
        const updatedInfo = await getBillingInfo()
        setBillingInfo(updatedInfo)
        setCreditAmount("")
        setSelectedPaymentMethod(null)
        setSuccessMessage("Credits added successfully")
        setShowSuccessModal(true)
      }
    } catch (err) {
      setError("Failed to add credit")
    } finally {
      setIsAddingCredit(false)
    }
  }

  const handlePaymentMethodSelection = (paymentMethodId: string) => {
    setSelectedPaymentMethod(paymentMethodId)
    setShowPaymentMethodModal(false)
    handleAddCredit()
  }

  const handleSelectPlan = async (priceId: string) => {
    if (!stripe) {
      setError("Stripe is not initialized. Please try again in a moment.")
      return
    }

    setIsProcessingSubscription(true)
    setError(null)

    try {
      const { clientSecret } = await createSubscription(priceId)

      if (paymentMethods.length === 1) {
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethods[0].id,
        })

        if (result.error) {
          setError(result.error.message || "An error occurred while processing your subscription")
        } else {
          const updatedInfo = await getBillingInfo()
          setBillingInfo(updatedInfo)
          setSuccessMessage("Subscription activated successfully")
          setShowSuccessModal(true)
          setShowPlansModal(false)
        }
      } else {
        setSelectedPaymentMethod(null)
        setShowPaymentMethodModal(true)
        sessionStorage.setItem("subscriptionClientSecret", clientSecret)
      }
    } catch (err) {
      setError("Failed to create subscription")
    } finally {
      setIsProcessingSubscription(false)
    }
  }

  const handleSubscriptionPaymentMethodSelection = async (paymentMethodId: string) => {
    if (!stripe) {
      setError("Stripe is not initialized. Please try again in a moment.")
      return
    }

    setIsProcessingSubscription(true)
    setError(null)

    try {
      const clientSecret = sessionStorage.getItem("subscriptionClientSecret")
      if (!clientSecret) {
        throw new Error("Client secret not found")
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId,
      })

      if (result.error) {
        setError(result.error.message || "An error occurred while processing your subscription")
      } else {
        const updatedInfo = await getBillingInfo()
        setBillingInfo(updatedInfo)
        setSuccessMessage("Subscription activated successfully")
        setShowSuccessModal(true)
        setShowPlansModal(false)
        setShowPaymentMethodModal(false)
      }
    } catch (err) {
      setError("Failed to process subscription")
    } finally {
      setIsProcessingSubscription(false)
      sessionStorage.removeItem("subscriptionClientSecret")
    }
  }

  const handleCancelSubscription = async () => {
    if (!billingInfo?.subscription_object?.id) {
      setError("Subscription ID not found")
      return
    }

    setShowCancelModal(true)
  }

  const confirmCancelSubscription = async () => {
    if (!billingInfo?.subscription_object?.id) {
      setError("Subscription ID not found")
      return
    }

    setIsProcessingSubscription(true)
    setError(null)

    try {
      const response = await fetch(
        process.env.REACT_APP_API_HOST + `billing/cancel-subscription/${billingInfo.subscription_object.id}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            Authorization: "Bearer " + localStorage.getItem("tokenLida"),
          },
        },
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      await response.json()
      const updatedInfo = await getBillingInfo()
      setBillingInfo(updatedInfo)
      setSuccessMessage("Subscription cancelled successfully")
      setShowSuccessModal(true)
      setShowCancelModal(false)
    } catch (err) {
      console.error("Error cancelling subscription:", err)
      setError(err instanceof Error ? err.message : "Failed to cancel subscription")
    } finally {
      setIsProcessingSubscription(false)
    }
  }

  const handleReactivateSubscription = async () => {
    if (!billingInfo?.subscription_object?.id) {
      setError("Subscription ID not found")
      return
    }

    if (!stripe) {
      setError("Stripe is not initialized. Please try again in a moment.")
      return
    }

    setIsProcessingSubscription(true)
    setError(null)

    try {
      const priceId = billingInfo.subscription_object.plan.id
      const { clientSecret } = await createSubscription(priceId)

      if (paymentMethods.length === 1) {
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethods[0].id,
        })

        if (result.error) {
          setError(result.error.message || "An error occurred while processing your subscription")
        } else {
          const updatedInfo = await getBillingInfo()
          setBillingInfo(updatedInfo)
          setSuccessMessage("Subscription activated successfully")
          setShowSuccessModal(true)
        }
      } else {
        setSelectedPaymentMethod(null)
        setShowPaymentMethodModal(true)
        sessionStorage.setItem("subscriptionClientSecret", clientSecret)
      }
    } catch (err) {
      console.error("Error reactivating subscription:", err)
      setError(err instanceof Error ? err.message : "Failed to reactivate subscription")
    } finally {
      setIsProcessingSubscription(false)
    }
  }

  const handleOutsideClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setShowPlansModal(false)
      setShowPaymentMethodModal(false)
      setShowCancelModal(false)
      setShowSuccessModal(false)
    }
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger justify-content-between items-center p-3 w-full d-flex" role="alert">
        <span>
          {error === "Stripe is not initialized. Please try again in a moment." ? (
            <strong>Stripe is not initialized. Please try again in a moment.</strong>
          ) : (
            error
          )}
        </span>
        <button onClick={() => {
          setError(null)
          setShowPaymentMethodModal(false)
        }} className="text-white hover:text-gray-300 bg-transparent border-0">
          <X size={20} color="#58151c" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2>Current Plan</h2>
      {billingInfo?.is_subscribed ? (
        <>
          <p>Plan: {billingInfo.subscription_object?.plan.interval === "year" ? "Yearly" : "Monthly"} Subscription</p>
          <p>
            Price: {(billingInfo.subscription_object?.plan.amount ?? 0) / 100}{" "}
            {billingInfo.subscription_object?.plan.currency.toUpperCase()}/
            {billingInfo.subscription_object?.plan.interval}
          </p>
          {billingInfo.subscription_object?.cancel_at_period_end ? (
            <p>
              Cancels at:{" "}
              {new Date((billingInfo.subscription_object?.current_period_end ?? 0) * 1000).toLocaleDateString()}
            </p>
          ) : (
            <p>
              Next invoice date:{" "}
              {new Date((billingInfo.subscription_object?.current_period_end ?? 0) * 1000).toLocaleDateString()}
            </p>
          )}
          {billingInfo.subscription_object?.cancel_at_period_end ? (
            <button
              className="btn btn-primary"
              onClick={handleReactivateSubscription}
              disabled={isProcessingSubscription}
            >
              {isProcessingSubscription ? "Processing..." : "Reactivate Subscription"}
            </button>
          ) : (
            <button className="btn btn-danger" onClick={handleCancelSubscription} disabled={isProcessingSubscription}>
              {isProcessingSubscription ? "Processing..." : "Cancel Subscription"}
            </button>
          )}
        </>
      ) : (
        <>
          <p>Plan: Pay as you go</p>
          <p>
            Credit Balance: {billingInfo?.credit_balance ?? 0} {billingInfo?.currency?.toUpperCase() ?? "EUR"}
          </p>
        </>
      )}

      {!billingInfo?.is_subscribed && (
        <div className="mt-4">
          <h3>Add Credits</h3>
          <div className="input-group mb-3">
            <input
              type="number"
              className="form-control"
              placeholder="Enter amount (min 10 €)"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              min="10"
            />
            <button
              className="btn btn-primary"
              type="button"
              onClick={handleAddCredit}
              disabled={
                isAddingCredit ||
                !creditAmount ||
                paymentMethods.length === 0 ||
                Number.parseFloat(creditAmount) < 10 ||
                !stripe
              }
            >
              {isAddingCredit ? "Processing..." : "Add Credit"}
            </button>
            {paymentMethods.length === 0 && (
              <div className="input-group-append">
                <span className="input-group-text bg-transparent border-0">
                  <div className="position-relative">
                    <AlertCircle className="text-danger" size={20} />
                    <span className="tooltip-text">Please add a payment method first</span>
                  </div>
                </span>
              </div>
            )}
          </div>
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      )}

      <div className="mt-4 d-flex align-items-center">
        <button
          className="btn btn-primary"
          onClick={() => setShowPlansModal(true)}
          disabled={paymentMethods.length === 0}
        >
          Change Plan
        </button>
        {paymentMethods.length === 0 && (
          <div className="ms-2">
            <div className="position-relative">
              <AlertCircle className="text-danger" size={20} />
              <span className="tooltip-text">Please add a payment method first</span>
            </div>
          </div>
        )}
      </div>

      {/* Plans Modal */}
      {showPlansModal && billingInfo && (
        <div className="modal-backdrop" onClick={(e) => handleOutsideClick(e)}>
          <div className="modal d-block" tabIndex={-1} role="dialog" ref={modalRef}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Available Plans</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPlansModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {Array.isArray(billingInfo.upsell_plans) && billingInfo.upsell_plans.length > 0 ? (
                    billingInfo.upsell_plans.map((plan) => (
                      <div key={plan.price_id} className="card mb-2">
                        <div className="card-body">
                          <h5 className="card-title">
                            {plan.period.charAt(0).toUpperCase() + plan.period.slice(1)}ly Plan
                          </h5>
                          <p className="card-text">
                            Price: {plan.price} {plan.currency.toUpperCase()}
                          </p>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleSelectPlan(plan.price_id)}
                            disabled={isProcessingSubscription}
                          >
                            {isProcessingSubscription ? "Processing..." : "Select Plan"}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="alert alert-warning">No plans available</div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPlansModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Selection Modal */}
      {showPaymentMethodModal && (
        <div className="modal-backdrop" onClick={(e) => handleOutsideClick(e)}>
          <div className="modal d-block" tabIndex={-1} role="dialog" ref={modalRef}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Select Payment Method</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPaymentMethodModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className={`card mb-2 ${method.default ? "border-primary" : ""}`}>
                      <div className="card-body d-flex justify-content-between align-items-center">
                        <div>
                          <CreditCard className="me-2" />
                          {method.card.brand.charAt(0).toUpperCase() + method.card.brand.slice(1)} ending in{" "}
                          {method.card.last4}
                          {method.default && <span className="badge bg-primary ms-2">Default</span>}
                        </div>
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => {
                            if (sessionStorage.getItem("subscriptionClientSecret")) {
                              handleSubscriptionPaymentMethodSelection(method.id)
                            } else {
                              handlePaymentMethodSelection(method.id)
                            }
                          }}
                          disabled={isProcessingSubscription}
                        >
                          {isProcessingSubscription ? "Processing..." : "Select"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <div className="modal-backdrop" onClick={(e) => handleOutsideClick(e)}>
          <div className="modal d-block" tabIndex={-1} role="dialog" ref={modalRef}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Cancel Subscription</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCancelModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to cancel your subscription?</p>
                  <p>This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmCancelSubscription}
                    disabled={isProcessingSubscription}
                  >
                    {isProcessingSubscription ? "Processing..." : "Confirm Cancellation"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-backdrop" onClick={(e) => handleOutsideClick(e)}>
          <div className="modal d-block" tabIndex={-1} role="dialog" ref={modalRef}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Success</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSuccessModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex align-items-center justify-content-center">
                    <CheckCircle className="text-success me-3" size={48} />
                    <p className="m-0">{successMessage}</p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={() => setShowSuccessModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{modalStyles}</style>
    </div>
  )
}
